import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Tag, TripNote } from "@poi-planner/shared";
import { useUIStore } from "../store/uiStore";
import { useTripStore } from "../store/tripStore";
import {
  flushLibrarySave,
  poiFromPlace,
  useLibraryStore,
} from "../store/libraryStore";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { EditorTitleInput } from "./FormFields";
import { EventEditorAlert } from "./EventEditorSection";
import {
  DisclosureSummary,
  EditorDisclosure,
  EditorSection,
} from "./EditorSection";
import { ReadableTextField } from "./ReadableTextField";
import { PoiCombobox, type PoiComboboxValue } from "./PoiCombobox";
import { TagChipInput } from "./TagChipInput";
import { SegmentedControl } from "./SegmentedControl";
import { rankNoteTags } from "../utils/noteTags";
import { DEFAULT_PHOSPHOR_ICON, toPhosphorTagIcon } from "../utils/tagIcons";
import { daysBetweenDates, epochMs } from "../utils/zonedTime";
import {
  describeEventSpan,
  formatEndDayOffset,
  formatSpanDuration,
  formatZoneShift,
} from "../utils/eventSpan";
import {
  DateChip,
  EventTimingRows,
  TimezoneChangeNotice,
  eventTimeFieldsError,
  eventTimeFieldsFromZoned,
  zonedFromEventTimeFields,
  type EventTimeFieldsValue,
} from "./EventTimingFields";

type TimingMode = "timed" | "all-day" | "inbox";

const TIMING_MODE_OPTIONS: {
  value: TimingMode;
  label: string;
  icon: string;
}[] = [
  { value: "timed", label: "Timed", icon: "schedule" },
  { value: "all-day", label: "All day", icon: "calendar_today" },
  { value: "inbox", label: "Unscheduled", icon: "inbox" },
];

function emptyTimeFields(zone: string): EventTimeFieldsValue {
  return { date: "", time: "", zone, offset: null };
}

function endpointFromPoiId(poiId: string | null): PoiComboboxValue | null {
  return poiId ? { kind: "poi", poiId } : null;
}

/** Live read-out under the timing fields: duration, next-day, zone shift. */
function TimingSummary({
  mode,
  start,
  end,
  allDayStartDate,
  allDayEndDate,
}: {
  mode: TimingMode;
  start: EventTimeFieldsValue;
  end: EventTimeFieldsValue;
  allDayStartDate: string;
  allDayEndDate: string;
}) {
  if (mode === "inbox") {
    return (
      <p className="m-0 flex items-center gap-2 rounded-lg bg-subtle px-3 py-2.5 text-sm text-secondary">
        <span className="material-symbols-outlined text-lg" aria-hidden>
          inbox
        </span>
        Stays in the inbox until it has a time. You can also drag it onto the
        calendar.
      </p>
    );
  }

  let items: string[] = [];
  let problem: string | null = null;
  if (mode === "all-day") {
    if (allDayStartDate && allDayEndDate) {
      const days = daysBetweenDates(allDayStartDate, allDayEndDate) + 1;
      if (days < 1) problem = "The last day is before the first day.";
      else items = [`${days} day${days === 1 ? "" : "s"}`];
    }
  } else {
    const startTime =
      !eventTimeFieldsError(start) && zonedFromEventTimeFields(start);
    const endTime = !eventTimeFieldsError(end) && zonedFromEventTimeFields(end);
    if (startTime && endTime) {
      const span = describeEventSpan(startTime, endTime);
      if (span.minutes <= 0) {
        problem = "Ends before it starts. Check the dates and time zones.";
      } else {
        items = [
          formatSpanDuration(span.minutes),
          formatEndDayOffset(span.endDayOffset),
          formatZoneShift(span),
        ].filter((item): item is string => !!item);
      }
    }
  }

  if (problem) {
    return (
      <p className="m-0 mt-3 text-sm font-semibold text-error">{problem}</p>
    );
  }
  if (!items.length) return null;
  return (
    <p
      className="m-0 mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-secondary"
      aria-live="polite"
    >
      {items.map((item, index) => (
        <span
          key={item}
          className={index === 0 ? "font-bold tabular-nums text-primary" : ""}
        >
          {item}
        </span>
      ))}
    </p>
  );
}

/**
 * Create/edit trip notes — the replacement for the kanban's "Add Transport"
 * flow. Transport is a note tagged with a mode; start/end POI refs give the
 * route subtitle and keep travel estimates working. Start/end zones are
 * editable independently (flights).
 */
export function NoteEditorModal() {
  const activeEditor = useUIStore((s) => s.activeEditor);
  const noteDraftTiming = useUIStore((s) => s.noteDraftTiming);
  const timingProposal = useUIStore((s) => s.timingProposal);
  const closeEditor = useUIStore((s) => s.closeEditor);

  const notes = useTripStore((s) => s.schedule.notes);
  const trip = useTripStore((s) => s.trip);
  const createNote = useTripStore((s) => s.createNote);
  const updateNote = useTripStore((s) => s.updateNote);
  const deleteNote = useTripStore((s) => s.deleteNote);
  const isReadOnly = useTripStore((s) => s.isReadOnly);

  const tags = useLibraryStore((s) => s.tags);
  const pois = useLibraryStore((s) => s.pois);
  const createPOI = useLibraryStore((s) => s.createPOI);
  const createTag = useLibraryStore((s) => s.createTag);

  const isOpen = activeEditor?.type === "note";
  const isCreate = isOpen && activeEditor.mode === "create";
  const editingNote: TripNote | undefined =
    isOpen && activeEditor.entityId
      ? notes.find((note) => note.id === activeEditor.entityId)
      : undefined;

  const defaultZone = trip?.defaultTimezone ?? "UTC";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  // Leg membership isn't edited here, but is carried through so a save keeps
  // whatever the note already belongs to.
  const [legIds, setLegIds] = useState<string[]>([]);
  const [from, setFrom] = useState<PoiComboboxValue | null>(null);
  const [to, setTo] = useState<PoiComboboxValue | null>(null);
  const [locked, setLocked] = useState(false);
  const [mode, setMode] = useState<TimingMode>("inbox");
  const [allDayStartDate, setAllDayStartDate] = useState("");
  const [allDayEndDate, setAllDayEndDate] = useState("");
  const [start, setStart] = useState<EventTimeFieldsValue>(() =>
    emptyTimeFields(defaultZone),
  );
  const [end, setEnd] = useState<EventTimeFieldsValue>(() =>
    emptyTimeFields(defaultZone),
  );
  const [routeOpen, setRouteOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fromInputRef = useRef<HTMLInputElement>(null);

  // (Re)hydrate whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSaving(false);
    if (editingNote) {
      const proposal =
        timingProposal?.eventType === "note" &&
        timingProposal.entityId === editingNote.id
          ? timingProposal
          : null;
      const startTime = proposal?.startTime ?? editingNote.startTime;
      const endTime = proposal?.endTime ?? editingNote.endTime;
      const nextFrom = endpointFromPoiId(editingNote.startPoiId);
      const nextTo = endpointFromPoiId(editingNote.endPoiId);
      setName(editingNote.name);
      setDescription(editingNote.description ?? "");
      setTagIds(editingNote.tagIds);
      setLegIds(editingNote.legIds ?? []);
      setFrom(nextFrom);
      setTo(nextTo);
      setLocked(editingNote.locked);
      setMode(
        editingNote.allDayStartDate !== null
          ? "all-day"
          : startTime
            ? "timed"
            : "inbox",
      );
      setAllDayStartDate(editingNote.allDayStartDate ?? "");
      setAllDayEndDate(editingNote.allDayEndDate ?? "");
      setStart(
        startTime
          ? eventTimeFieldsFromZoned(startTime)
          : emptyTimeFields(defaultZone),
      );
      setEnd(
        endTime
          ? eventTimeFieldsFromZoned(endTime)
          : emptyTimeFields(defaultZone),
      );
      setRouteOpen(!!(nextFrom || nextTo));
      setTagsOpen(editingNote.tagIds.length > 0);
    } else {
      setName("");
      setDescription("");
      setTagIds([]);
      setLegIds([]);
      setFrom(null);
      setTo(null);
      setLocked(false);
      setMode(
        noteDraftTiming?.type === "all-day"
          ? "all-day"
          : noteDraftTiming?.type === "timed"
            ? "timed"
            : "inbox",
      );
      setAllDayStartDate(
        noteDraftTiming?.type === "all-day" ? noteDraftTiming.startDate : "",
      );
      setAllDayEndDate(
        noteDraftTiming?.type === "all-day" ? noteDraftTiming.endDate : "",
      );
      setStart(
        noteDraftTiming?.type === "timed"
          ? eventTimeFieldsFromZoned(noteDraftTiming.startTime)
          : emptyTimeFields(defaultZone),
      );
      setEnd(
        noteDraftTiming?.type === "timed"
          ? eventTimeFieldsFromZoned(noteDraftTiming.endTime)
          : emptyTimeFields(defaultZone),
      );
      setRouteOpen(false);
      setTagsOpen(false);
    }
  }, [defaultZone, editingNote, isOpen, noteDraftTiming, timingProposal]);

  const activeTimingProposal =
    editingNote &&
    timingProposal?.eventType === "note" &&
    timingProposal.entityId === editingNote.id
      ? timingProposal
      : null;

  const readOnly = isReadOnly();

  const changeMode = (next: TimingMode) => {
    // Each mode keeps its own fields, so switching back restores them; only
    // seed the new mode's dates from the other one when it has none yet.
    if (next === "all-day" && !allDayStartDate) {
      const fallbackDate =
        start.date || editingNote?.startTime?.slice(0, 10) || trip?.startDate;
      if (fallbackDate) {
        setAllDayStartDate(fallbackDate);
        setAllDayEndDate(
          end.date && end.date >= fallbackDate ? end.date : fallbackDate,
        );
      }
    }
    if (next === "timed" && !start.date && allDayStartDate) {
      setStart({ ...start, date: allDayStartDate });
      setEnd({ ...end, date: allDayEndDate || allDayStartDate });
    }
    setMode(next);
  };

  const endpointName = useCallback(
    (endpoint: PoiComboboxValue | null) =>
      !endpoint
        ? null
        : endpoint.kind === "place"
          ? endpoint.place.name
          : (pois.find((poi) => poi.id === endpoint.poiId)?.name ?? null),
    [pois],
  );

  const rankTags = useCallback(
    (matches: Tag[]) => rankNoteTags(matches, notes, tagIds),
    [notes, tagIds],
  );

  const tagNames = useMemo(
    () =>
      tagIds
        .map((id) => tags.find((tag) => tag.id === id)?.name)
        .filter((tagName): tagName is string => !!tagName),
    [tagIds, tags],
  );

  const createNoteTag = (tagName: string) =>
    createTag({
      name: tagName,
      icon: toPhosphorTagIcon(DEFAULT_PHOSPHOR_ICON),
    });

  /** Validates the form; returns the timing half of the payload or null. */
  const validateTiming = () => {
    if (mode === "all-day") {
      if (!allDayStartDate || !allDayEndDate) {
        setError("Set both the first and last day.");
        return null;
      }
      if (allDayEndDate < allDayStartDate) {
        setError("The last day must be on or after the first day.");
        return null;
      }
      if (
        trip &&
        (allDayStartDate < trip.startDate || allDayEndDate > trip.endDate)
      ) {
        setError("All-day notes must stay within the trip dates.");
        return null;
      }
      return {
        startTime: null,
        endTime: null,
        allDayStartDate,
        allDayEndDate,
      };
    }
    if (mode === "inbox") {
      return {
        startTime: null,
        endTime: null,
        allDayStartDate: null,
        allDayEndDate: null,
      };
    }
    if (!(start.date && start.time && end.date && end.time)) {
      setError("Add a start and end time, or choose Unscheduled.");
      return null;
    }
    const fieldError = eventTimeFieldsError(start) ?? eventTimeFieldsError(end);
    if (fieldError) {
      setError(fieldError);
      return null;
    }
    const startTime = zonedFromEventTimeFields(start);
    const endTime = zonedFromEventTimeFields(end);
    if (!startTime || !endTime) {
      setError("Choose valid start and end wall times.");
      return null;
    }
    if (epochMs(endTime) <= epochMs(startTime)) {
      setError("The end time must be after the start time.");
      return null;
    }
    return { startTime, endTime, allDayStartDate: null, allDayEndDate: null };
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Give the note a name.");
      return;
    }
    const timing = validateTiming();
    if (!timing) return;

    // Google Places picks become library POIs now, at save time. Both ends
    // can be the same new place, so create each place once.
    const createdByPlaceId = new Map<string, string>();
    const resolveEndpoint = (endpoint: PoiComboboxValue | null) => {
      if (!endpoint) return null;
      if (endpoint.kind === "poi") return endpoint.poiId;
      const existing = createdByPlaceId.get(endpoint.place.placeId);
      if (existing) return existing;
      const id = createPOI(poiFromPlace(endpoint.place));
      createdByPlaceId.set(endpoint.place.placeId, id);
      return id;
    };
    const startPoiId = resolveEndpoint(from);
    const endPoiId = resolveEndpoint(to);

    if (createdByPlaceId.size > 0) {
      // Point the form at the new POIs so a retry after a failed save
      // doesn't create them again.
      setFrom(endpointFromPoiId(startPoiId));
      setTo(endpointFromPoiId(endPoiId));
      // The note references the POIs by foreign key, so their rows must
      // exist before the trip autosave writes the note.
      setSaving(true);
      await flushLibrarySave();
      setSaving(false);
      if (useLibraryStore.getState().saveStatus === "error") {
        setError(
          "Couldn't add the new place to your library. Check your connection and save again.",
        );
        return;
      }
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      ...timing,
      tagIds,
      startPoiId,
      endPoiId,
      locked,
      legIds,
    };

    if (isCreate) {
      createNote(payload);
    } else if (editingNote) {
      updateNote(editingNote.id, payload);
    }
    closeEditor();
  };

  const handleDelete = () => {
    if (!editingNote) return;
    deleteNote(editingNote.id);
    closeEditor();
  };

  const fromName = endpointName(from);
  const toName = endpointName(to);

  return (
    <Modal
      open={isOpen}
      onClose={closeEditor}
      title={isCreate ? "New note" : "Edit note"}
      description="Edit the note's name, description, timing, route and tags."
      icon="sticky_note_2"
      size="xl"
      footer={
        <>
          {!isCreate && editingNote && !readOnly && (
            <Button
              variant="danger"
              icon="delete"
              onClick={handleDelete}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button onClick={closeEditor} variant="secondary">
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button
              onClick={() => void handleSave()}
              variant="primary"
              loading={saving}
            >
              Save note
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {activeTimingProposal && (
          <TimezoneChangeNotice proposal={activeTimingProposal} />
        )}

        <div className="flex flex-col gap-2">
          <EditorTitleInput
            value={name}
            onChange={setName}
            placeholder="e.g. Shinkansen NOZOMI 214"
            autoFocus
            disabled={readOnly}
          />
          <ReadableTextField
            id="note-description"
            label="Description"
            value={description}
            onChange={setDescription}
            placeholder="Booking reference, seats, platform, links…"
            disabled={readOnly}
          />
        </div>

        <EditorSection
          id="note-timing-heading"
          title="Timing"
          icon="schedule"
          actions={
            mode !== "inbox" && (
              <Button
                size="sm"
                icon={locked ? "lock" : "lock_open"}
                selected={locked}
                disabled={readOnly}
                onClick={() => setLocked(!locked)}
                title={
                  mode === "all-day"
                    ? "Locked dates can't be changed by dragging"
                    : "Booked — locked items can't be moved or resized"
                }
              >
                {locked
                  ? "Locked"
                  : mode === "all-day"
                    ? "Lock dates"
                    : "Lock time"}
              </Button>
            )
          }
        >
          <div className="mb-3">
            {readOnly ? null : (
              <SegmentedControl
                aria-label="Timing"
                fullWidth
                options={TIMING_MODE_OPTIONS}
                value={mode}
                onChange={changeMode}
              />
            )}
          </div>

          {mode === "timed" && (
            <EventTimingRows
              start={start}
              end={end}
              onStartChange={setStart}
              onEndChange={setEnd}
              disabled={readOnly}
            />
          )}
          {mode === "all-day" && (
            <div className="grid grid-cols-1 items-center gap-x-3 gap-y-1.5 sm:grid-cols-[3.25rem_minmax(0,1fr)] sm:gap-y-2">
              <span className="text-sm font-semibold text-secondary max-sm:mt-1 max-sm:text-xs">
                From
              </span>
              <div>
                <DateChip
                  value={allDayStartDate}
                  onChange={setAllDayStartDate}
                  min={trip?.startDate}
                  max={trip?.endDate}
                  disabled={readOnly}
                  label="First day"
                />
              </div>
              <span className="text-sm font-semibold text-secondary max-sm:mt-1 max-sm:text-xs">
                To
              </span>
              <div>
                <DateChip
                  value={allDayEndDate}
                  onChange={setAllDayEndDate}
                  min={allDayStartDate || trip?.startDate}
                  max={trip?.endDate}
                  disabled={readOnly}
                  label="Last day"
                />
              </div>
            </div>
          )}
          <TimingSummary
            mode={mode}
            start={start}
            end={end}
            allDayStartDate={allDayStartDate}
            allDayEndDate={allDayEndDate}
          />
        </EditorSection>

        <div>
          <EditorDisclosure
            id="note-route-heading"
            title="Route"
            icon="alt_route"
            open={routeOpen}
            onOpenChange={(open) => {
              setRouteOpen(open);
              if (open && !from && !to && !readOnly) {
                requestAnimationFrame(() => fromInputRef.current?.focus());
              }
            }}
            summary={
              fromName || toName ? (
                <DisclosureSummary
                  text={`${fromName ?? "…"} → ${toName ?? "…"}`}
                />
              ) : null
            }
            emptyLabel="Add where it starts and ends"
          >
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
              <PoiCombobox
                label="From"
                icon="trip_origin"
                value={from}
                onChange={setFrom}
                disabled={readOnly}
                inputRef={fromInputRef}
              />
              <Button
                variant="icon"
                icon="swap_horiz"
                aria-label="Swap From and To"
                title="Swap"
                disabled={readOnly || (!from && !to)}
                onClick={() => {
                  setFrom(to);
                  setTo(from);
                }}
                className="justify-self-center sm:mt-6 max-sm:rotate-90"
              />
              <PoiCombobox
                label="To"
                icon="location_on"
                value={to}
                onChange={setTo}
                disabled={readOnly}
              />
            </div>
            <p className="mb-0 mt-3 text-xs text-tertiary">
              Searches your library first, then Google Places.
            </p>
          </EditorDisclosure>

          <EditorDisclosure
            id="note-tags-heading"
            title="Tags"
            icon="label"
            open={tagsOpen}
            onOpenChange={setTagsOpen}
            summary={
              tagNames.length ? (
                <DisclosureSummary
                  text={tagNames.join(", ")}
                  count={tagNames.length}
                />
              ) : null
            }
            emptyLabel="Add tags"
          >
            <TagChipInput
              inputId="note-tag-input"
              tags={tagIds}
              allTags={tags}
              onChange={setTagIds}
              onCreateTag={createNoteTag}
              rank={rankTags}
              disabled={readOnly}
            />
          </EditorDisclosure>
        </div>

        {error && <EventEditorAlert>{error}</EventEditorAlert>}
      </div>
    </Modal>
  );
}
